import React, { useState, useEffect } from 'react';
import classnames from 'classnames/bind';
import jwt_decode from 'jwt-decode';

import { notify } from '@NOTIFICATION/Notificator';

import useAuth from '@HOOKS/useAuth';
import { useGetAllWorkspaces, useSwitchWorkspaceMutation } from '@SERVICES';

import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { JwtPayload } from '@REDUCERS';

import styles from './WorkspaceSwitcher.module.scss';

const cx: CX = classnames.bind(styles);

function WorkspaceSwitcher() {
    const { state: { user }, actions: authActions } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    
    // Get workspaces - only fetch if user is super admin
    const { data: workspaces = [], isLoading: isLoadingWorkspaces } = useGetAllWorkspaces(undefined, {
        skip: !user?.workspaceId, // Skip if no user or workspace info
    });
    
    const [switchWorkspace] = useSwitchWorkspaceMutation();

    // Check if user is super admin by looking at JWT token
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    
    useEffect(() => {
        const token = userRepository.getData();
        if (token) {
            try {
                const decoded: JwtPayload = jwt_decode(token);
                setIsSuperAdmin(decoded.is_super_admin || false);
            } catch (error) {
                console.error('Error decoding token:', error);
                setIsSuperAdmin(false);
            }
        }
    }, [user]);

    const handleWorkspaceSwitch = async (workspaceId: string) => {
        if (!workspaceId || workspaceId === user?.workspaceId?.toString()) {
            return;
        }

        console.log('🔄 Starting workspace switch to ID:', workspaceId);
        setIsLoading(true);
        
        try {
            const result = await switchWorkspace({
                workspace_id: parseInt(workspaceId, 10),
            }).unwrap();

            console.log('✅ Workspace switch completed:', result);

            // The auth reducer matcher handles token storage and Redux state update
            // The API onQueryStarted handler handles cache invalidation
            // Just show success message and reload after a short delay
            notify.success(`Switched to workspace: ${result.workspace.name}`, {
                toastId: 'workspace-switch-success',
            });

            // Short delay to ensure all state updates are complete, then reload
            setTimeout(() => {
                console.log('🔄 Reloading page to ensure clean state...');
                window.location.reload();
            }, 800);
        } catch (error: any) {
            console.error('❌ Workspace switch failed:', error);
            notify.error(error?.data?.message || 'Failed to switch workspace', {
                toastId: 'workspace-switch-error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (!user?.workspaceName) {
        return null;
    }

    // For non-super admins, just show the workspace name
    if (!isSuperAdmin) {
        return (
            <span className={cx('workspace-name')}>
                (
                {user.workspaceName}
                )
            </span>
        );
    }

    // For super admins, show a dropdown
    return (
        <div className={cx('workspace-switcher')}>
            <select 
                value={user.workspaceId || ''} 
                onChange={(e) => handleWorkspaceSwitch(e.target.value)}
                disabled={isLoading || isLoadingWorkspaces}
                className={cx('workspace-dropdown')}
            >
                {workspaces.map((workspace) => (
                    <option key={workspace.id} value={workspace.id}>
                        {workspace.name}
                    </option>
                ))}
                {workspaces.length === 0 && user.workspaceName && (
                    <option value={user.workspaceId || ''}>
                        {user.workspaceName}
                    </option>
                )}
            </select>
            {isLoading && (
                <span className={cx('loading-indicator')}>
                    Switching...
                </span>
            )}
        </div>
    );
}

export default WorkspaceSwitcher; 